import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, PackageCheck, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { acquisitionsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { formatDate, orDash } from '@/utils/format';
import { AcquisitionStatusBadge, Field, Money, Stat, humanise } from './shared';

/**
 * One order, and recording what turned up.
 *
 * <p>Receiving is where stock is created: each received copy is accessioned into
 * the catalogue with its own number. The delivery form starts pre-filled with
 * what is still owed, because that is the common case, but every line can be
 * edited down — a box that is two short must be recorded as two short, or the
 * catalogue claims copies the shelves do not have.
 */
export function AcquisitionDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.ACQUISITION_MANAGE);

  const [receiving, setReceiving] = useState(false);
  const [received, setReceived] = useState<Record<string, string>>({});

  const acquisition = useQuery({
    queryKey: queryKeys.acquisitions.detail(id),
    queryFn: () => acquisitionsApi.get(id),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.acquisitions.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryInventory.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
  };

  const order = useMutation({
    mutationFn: () => acquisitionsApi.markOrdered(id),
    onSuccess: () => {
      toast.success('Order placed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const receive = useMutation({
    mutationFn: () =>
      acquisitionsApi.receive(
        id,
        Object.entries(received)
          .map(([itemId, quantity]) => ({ itemId, receivedQuantity: Number(quantity) }))
          .filter((line) => Number.isFinite(line.receivedQuantity)),
      ),
    onSuccess: (updated) => {
      const total = updated.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
      const ordered = updated.items.reduce((sum, item) => sum + item.quantity, 0);
      toast.success(
        updated.status === 'RECEIVED'
          ? `All ${total} copies are on the shelves`
          : `${total} of ${ordered} received — the order stays open`,
      );
      setReceiving(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => acquisitionsApi.cancel(id, reason),
    onSuccess: () => {
      toast.info('Order cancelled');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  if (acquisition.isError) {
    return <ErrorState error={acquisition.error} onRetry={() => void acquisition.refetch()} />;
  }
  if (acquisition.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = acquisition.data;
  const ordered = data.items.reduce((sum, item) => sum + item.quantity, 0);
  const arrived = data.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
  const receivable = data.status === 'ORDERED' || data.status === 'PARTIALLY_RECEIVED';

  function openReceipt() {
    // Pre-filled with what is still owed: the common case is that the rest of
    // the order arrived.
    setReceived(
      Object.fromEntries(
        data.items.map((item) => [item.id, String(item.quantity - item.receivedQuantity)]),
      ),
    );
    setReceiving(true);
  }

  return (
    <div>
      <Link
        to={ROUTES.acquisitions}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Acquisitions
      </Link>

      <PageHeader
        title={data.reference}
        description={`${orDash(data.supplierName)} · ${humanise(data.source)} · ${formatDate(data.orderDate)}`}
        icon={<ShoppingCart className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex items-center gap-2">
            <AcquisitionStatusBadge status={data.status} />
            {manages && data.status === 'DRAFT' && (
              <Button onClick={() => order.mutate()} disabled={order.isPending}>
                Place the order
              </Button>
            )}
            {manages && receivable && (
              <Button onClick={openReceipt}>
                <PackageCheck className="h-4 w-4" aria-hidden="true" />
                Record a delivery
              </Button>
            )}
            {manages && (data.status === 'DRAFT' || data.status === 'ORDERED') && (
              <Button
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt('Why is the order being cancelled?');
                  if (reason !== null && reason.trim() !== '') {
                    cancel.mutate(reason.trim());
                  }
                }}
              >
                Cancel
              </Button>
            )}
          </span>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Titles" value={data.items.length} />
        <Stat label="Copies ordered" value={ordered} />
        <Stat
          label="Arrived"
          value={arrived}
          tone={arrived === ordered && ordered > 0 ? 'success' : arrived > 0 ? 'warning' : 'neutral'}
          hint={arrived < ordered ? `${ordered - arrived} still owed` : 'Complete'}
        />
        <Stat label="Cost" value={<Money value={data.totalCost} />} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <div className="border-b border-line px-5 py-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              What was ordered
            </h2>
          </div>
          {data.items.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted">
              Nothing on the order yet. A title has to be catalogued before it can be ordered — a
              school orders a book, not an idea of one.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {data.items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-ink">{item.bookTitle}</span>
                    <span className="block text-[11px] text-subtle">
                      {item.quantity} × <Money value={item.unitCost} />
                    </span>
                  </span>
                  <span className="text-right text-sm">
                    <span className="block text-ink">
                      {item.receivedQuantity} of {item.quantity}
                    </span>
                    <span className="block text-[11px] text-subtle">
                      {item.receivedQuantity === item.quantity
                        ? 'all here'
                        : `${item.quantity - item.receivedQuantity} owed`}
                    </span>
                  </span>
                  <Money value={item.lineTotal} bold />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Order</h2>
          <dl className="mt-2">
            <Field label="Supplier">{orDash(data.supplierName)}</Field>
            <Field label="Invoice">{orDash(data.invoiceNumber)}</Field>
            <Field label="Budget">{orDash(data.fundingSource)}</Field>
            <Field label="Ordered">{formatDate(data.orderDate)}</Field>
            <Field label="Last delivery">
              {data.receivedDate === undefined ? '—' : formatDate(data.receivedDate)}
            </Field>
            <Field label="Library">{data.libraryName}</Field>
            {data.cancellationReason !== undefined && (
              <Field label="Cancelled because">{data.cancellationReason}</Field>
            )}
          </dl>
        </Card>
      </div>

      <Modal
        open={receiving}
        onClose={() => setReceiving(false)}
        title="Record a delivery"
        description="Each copy recorded here is accessioned into the catalogue with its own number. Edit a line down if the box was short."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReceiving(false)}>
              Cancel
            </Button>
            <Button onClick={() => receive.mutate()} disabled={receive.isPending}>
              Accession them
            </Button>
          </>
        }
      >
        <ul className="divide-y divide-line">
          {data.items.map((item) => {
            const owed = item.quantity - item.receivedQuantity;
            return (
              <li key={item.id} className="flex items-center gap-3 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-ink">{item.bookTitle}</span>
                  <span className="block text-[11px] text-subtle">
                    {owed === 0 ? 'nothing outstanding' : `${owed} still owed`}
                  </span>
                </span>
                <input
                  type="number"
                  min={0}
                  max={owed}
                  className="w-20 border border-line bg-bg px-2 py-1 text-right text-sm text-ink"
                  value={received[item.id] ?? '0'}
                  disabled={owed === 0}
                  onChange={(event) =>
                    setReceived((current) => ({ ...current, [item.id]: event.target.value }))
                  }
                />
              </li>
            );
          })}
        </ul>
      </Modal>
    </div>
  );
}
